import { useMemo } from "react"

import {
  MUSTERHEFT_EXPORT_OVERLAY,
  PROJECT_ATTACHMENTS_OVERLAY,
  PROJECT_GAEB_OVERLAY,
  PROJECT_PDF_OVERLAY,
  SHARE_OVERLAY
} from "shared/context/overlay/types"
import { Logger } from "shared/lib/Logger"
import { useEnhanceCallbacks } from "shared/lib/useEnhanceCallbacks"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"

import { useDuplicateProject } from "entities/projects/api/useDuplicateProject"

export interface UseSharedProjectScopedActionHandlersArgs {
  projectID: string | null | undefined
  projectShareID: string
  onAction?: () => void
}

export function useSharedProjectScopedActionHandlers({
  projectID,
  projectShareID,
  onAction
}: UseSharedProjectScopedActionHandlersArgs) {
  const { openOverlay: openProjectGaebOverlay } =
    useOverlay(PROJECT_GAEB_OVERLAY)
  const { openOverlay: openMusterheftExportOverlay } = useOverlay(
    MUSTERHEFT_EXPORT_OVERLAY
  )
  const { openOverlay: openProjectPdfOverlay } = useOverlay(PROJECT_PDF_OVERLAY)
  const { openOverlay: openShareOverlay } = useOverlay(SHARE_OVERLAY)
  const { openOverlay: openProjectAttachments } = useOverlay(
    PROJECT_ATTACHMENTS_OVERLAY
  )
  const { duplicateProject } = useDuplicateProject()

  const handleGaebExport = useEvent(() => {
    openProjectGaebOverlay({ projectID: projectShareID, isShareID: true })
  })

  const handleMusterheftExport = useEvent(() => {
    openMusterheftExportOverlay({ projectID: projectShareID, isShareID: true })
  })

  const handleExport = useEvent(() => {
    openProjectPdfOverlay({ projectID: projectShareID, isShareID: true })
  })

  const handleShare = useEvent(() => {
    openShareOverlay({ projectID: projectShareID, isShareID: true })
  })

  const handleDuplicate = useEvent(() => {
    if (!projectID) {
      throw Error("Missing Project ID!")
    }
    // duplicateProject already toasts and rolls back the cache; catch so the
    // re-thrown reason does not surface as an unhandled rejection.
    duplicateProject({ projectID, shareID: projectShareID }).catch(e =>
      Logger.error(e, "Failed to duplicate shared project", { projectID })
    )
  })

  const handleOpenAttachments = useEvent(() => {
    openProjectAttachments({ projectID: projectShareID, isShareID: true })
  })

  const callbacks = useMemo(
    () => ({
      onDuplicate: handleDuplicate,
      onShare: handleShare,
      onExport: handleExport,
      onGaebExport: handleGaebExport,
      onMusterheftExport: handleMusterheftExport,
      onOpenAttachments: handleOpenAttachments
    }),
    [
      handleDuplicate,
      handleShare,
      handleExport,
      handleGaebExport,
      handleMusterheftExport,
      handleOpenAttachments
    ]
  )

  return useEnhanceCallbacks({ callbacks, onAction })
}
